import axios from "axios";
import { CommonStorage, TokenStorage } from "../storage";
import ODataStore from "devextreme/data/odata/store";
import DataSource from "devextreme/data/data_source";
import CacheKeys from "../cache/cache.keys";

const XREFERER_ORIGIN_PATH_KEY = "x-referer-origin-path";

import appConfig from "../../app-config";
import { store } from "../../_store";
import { CacheService } from "../cache/cache-service";
import { EncodeODataQuery } from "../../_helpers/network/requests";

var source = [];

var handleUnauthorized = (statusCode) => {
  switch (statusCode) {
    case 401:
    case 403:
      store.dispatch("authentication/logout");
      location.reload(true);
      break;
  }
};
axios.interceptors.request.use(
  (config) => {
    const token = TokenStorage.getToken();
    if (token) {
      config.headers.common[
        "Authorization"
      ] = `Bearer ${TokenStorage.getToken()}`;
      config.headers.common["x-tenant-id"] = TokenStorage.getTenant();
    }

    config.headers.common[XREFERER_ORIGIN_PATH_KEY] =
      CommonStorage.getItems("currentRoute");

    if (config.method == "post" || config.method == "put") {
      store.dispatch("ui/setIsSubmitting", true);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
axios.interceptors.response.use(
  (response) => {
    // this.$logger.debug("Response Intercepted", response);

    if (response.config.method == "post" || response.config.method == "put") {
      store.dispatch("ui/setIsSubmitting", false);
    }

    if (
      response.status === 200 ||
      response.status === 201 ||
      response.status === 204
    ) {
      return Promise.resolve(response);
    } else {
      return Promise.reject(response);
    }
  },
  (error) => {


    if (!error.response) {
      return Promise.reject({ message: 'We are unable to reach the GCORPcx API. Please check your internet connection before attempting to save again. If the issue persists please contact your system administrator.' });
    }

    if (error.response && (
      error.response.config.method == "post" ||
      error.response.config.method == "put")
    ) {
      store.dispatch("ui/setIsSubmitting", false);
    }

    if (error.response.config.url == "/Token") {
      return Promise.reject(error.response);
    } else if (error.response.status) {
      handleUnauthorized(error.response.status);
      return Promise.reject(error.response);
    }
  }
);

const ApiService = {
  init(baseURL) {
    axios.defaults.baseURL = baseURL;
  },

  setTokenHeader() {
    axios.defaults.headers.common.Authorization = `Bearer ${TokenStorage.getToken()}`;
    axios.defaults.headers.common["Content-Type"] = "application/json";
  },

  setTenantHeader() {
    axios.defaults.headers.common["x-tenant-id"] = TokenStorage.getTenant();
    axios.defaults.headers.common[XREFERER_ORIGIN_PATH_KEY] =
      CommonStorage.getItems("currentRoute");
  },

  setSessionHeader() {
    axios.defaults.headers.common["x-session-id"] = TokenStorage.getSessionId();
  },

  setHeaders() {
    ApiService.setTokenHeader();
    ApiService.setTenantHeader();
    ApiService.setSessionHeader();
  },

  removeHeader() {
    axios.defaults.headers.common = {};
  },

  get(resource, extra) {
    const CancelToken = axios.CancelToken;
    if (extra) {
      if (source[extra]) {
        source[extra].cancel();
      }
      source[extra] = CancelToken.source();
    }

    ApiService.setHeaders();

    // Encode only the query string part
    resource = EncodeODataQuery(resource);

    if (extra)
      return axios.get(resource, {
        cancelToken: source[extra].token,
      });
    else return axios.get(resource);
  },

  oDataGet(oDataQuery) {
    let odata = "";

    for (var propertyName in oDataQuery) {
      if (
        propertyName === "search" ||
        propertyName === "url" ||
        propertyName === "key"
      ) {
        continue;
      }

      if (oDataQuery[propertyName] != undefined) {
        if (odata !== "") {
          odata = odata + "&";
        }

        switch (propertyName) {
          case "orderby":
            odata = odata + "$orderby=" + oDataQuery[propertyName];
            break;

          case "top":
            odata = odata + "$top=" + oDataQuery[propertyName];
            break;

          case "skip":
            odata = odata + "$skip=" + oDataQuery[propertyName];
            break;

          case "filter":
            odata = odata + "$filter=" + oDataQuery[propertyName];
            break;

          case "expand":
            odata = odata + "$expand=" + oDataQuery[propertyName];
            break;

          case "format":
            odata = odata + "$format=" + oDataQuery[propertyName];
            break;

          case "select":
            if (!oDataQuery.hasOwnProperty("Apply")) {
              odata = odata + "$select=" + oDataQuery[propertyName];
            }
            break;

          case "count":
            odata = odata + "$count=true";
            break;

          case "apply":
            odata = odata + "$apply=" + oDataQuery[propertyName];
            break;
        }
      }
    }

    let resource = "";

    if (odata !== "") {
      resource = oDataQuery.url + "?" + odata;
    } else {
      resource = oDataQuery.url;
    }

    var cacheKeyIndex = CacheKeys.findIndex((x) => x.key == oDataQuery.url);

    // Encode only the query string part
    resource = EncodeODataQuery(resource);

    if (cacheKeyIndex == -1) {
      return axios.get(resource);
    } else {
      var isCached = CacheService.getCache(resource);
      if (!isCached) {
        ApiService.setHeaders();
        return new Promise(async (resolve, reject) => {
          try {
            var result = await axios.get(resource);
            CacheService.saveCache(resource, result, 5);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        });
      } else {
        return new Promise(async (resolve) => {
          resolve(isCached);
        });
      }
    }
  },
  createDataSource(url, params) {
    return new DataSource({
      filter: params.filter,
      sort: params.sort,
      expand: params.expand,
      paginate: !params.preventPagination,
      pageSize: params.pageSize || 20,
      store: new ODataStore({
        type: "odata",
        key: params.key || "",
        keyType: params.keyType || "",
        version: 4,
        url: `${appConfig.VUE_APP_ROOT_API}/${url}`,
        fieldTypes: params.fieldTypes || "",
        beforeSend: function (request) {
          request.headers[
            "Authorization"
          ] = `Bearer ${TokenStorage.getToken()}`;
          request.headers["Content-Type"] = "application/json";
          request.headers["x-tenant-id"] = TokenStorage.getTenant();
          request.headers[XREFERER_ORIGIN_PATH_KEY] =
            CommonStorage.getItems("currentRoute");

          if (params.serverPageRequested) params.serverPageRequested();
          request.timeout = 100000000;
        },
        errorHandler: (error) => {
          // this.$logger.debug("odata store error", error);
          handleUnauthorized(error.httpStatus);
        },
      }),
      select: params.select,
      postProcess: params.postProcess,
    });
  },

  post(resource, payload) {
    ApiService.setHeaders();
    return axios.post(resource, payload);
  },

  put(resource, key, payload, keyFieldType) {
    if (keyFieldType === "Guid" || keyFieldType === "Integer") {
      resource = resource + "(" + key + ")";
    } else {
      resource = resource + "('" + key + "')";
    }
    return axios.put(resource, payload);
  },

  patch(resource, key, payload, keyFieldType) {
    if (keyFieldType === "Guid" || keyFieldType === "Integer") {
      resource = resource + "(" + key + ")";
    } else {
      resource = resource + "('" + key + "')";
    }
    return axios.patch(resource, payload);
  },

  delete(resource, key, keyFieldType, payload) {
    if (keyFieldType === "Guid" || keyFieldType === "Integer") {
      resource = resource + "(" + key + ")";
    } else {
      resource = resource + "('" + key + "')";
    }
    if (payload) {
      return axios.delete(resource, { data: payload });
    } else return axios.delete(resource);
  },

  /**
   * Perform a custom Axios request.
   *
   * data is an object containing the following properties:
   *  - method
   *  - url
   *  - data ... request payload
   *  - auth (optional)
   *    - username
   *    - password
   * */
  customRequest(data) {
    return axios(data);
  },
};

export default ApiService;
